import { z } from 'zod';
import { transactionSchema } from './transactions';

const calendarYmdSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data deve ser YYYY-MM-DD.')
  .refine((value) => {
    const parsed = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
  }, 'Data inválida.');

export const reconciliationSourceSchema = z.object({
  kind: z.enum(['account', 'card']),
  id: z.string().trim().min(1).max(120).optional(),
  name: z.string().trim().min(1).max(120).optional(),
}).strict().refine((source) => Boolean(source.id) !== Boolean(source.name), {
  message: 'Informe exatamente id ou name para a fonte.',
});

export const normalizedStatementRowSchema = z.object({
  sourceRowId: z.string().trim().min(1).max(120),
  date: calendarYmdSchema,
  originalDescription: z.string().trim().min(1).max(240),
  amountCents: z.number().int().safe(),
  effectuated: z.boolean().optional(),
  time: z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/).nullable().optional(),
  balanceCents: z.number().int().safe().nullable().optional(),
}).strict();
export type NormalizedStatementRow = z.infer<typeof normalizedStatementRowSchema>;

const normalizedStatementRowsSchema = z.array(normalizedStatementRowSchema).min(1).max(200).superRefine((rows, ctx) => {
  const seen = new Set<string>();
  rows.forEach((row, index) => {
    if (seen.has(row.sourceRowId)) {
      ctx.addIssue({ code: 'custom', path: [index, 'sourceRowId'], message: 'sourceRowId duplicado.' });
    }
    seen.add(row.sourceRowId);
  });
});

export const previewStatementReconciliationBodySchema = z.object({
  source: reconciliationSourceSchema,
  rows: normalizedStatementRowsSchema,
  effectedOnly: z.boolean().optional(),
}).strict();
export type PreviewStatementReconciliationBody = z.infer<typeof previewStatementReconciliationBodySchema>;

export const prepareStatementImportBodySchema = z.object({
  documentSha256: z.string().regex(/^[a-f0-9]{64}$/, 'documentSha256 deve ser SHA-256 hexadecimal.'),
  source: reconciliationSourceSchema,
  rows: normalizedStatementRowsSchema,
  effectedOnly: z.boolean().optional(),
}).strict();
export type PrepareStatementImportBody = z.infer<typeof prepareStatementImportBodySchema>;

export const lookupStatementImportBodySchema = z.object({
  documentSha256: z.string().regex(/^[a-f0-9]{64}$/, 'documentSha256 deve ser SHA-256 hexadecimal.'),
  source: reconciliationSourceSchema,
}).strict();
export type LookupStatementImportBody = z.infer<typeof lookupStatementImportBodySchema>;

export const prepareNaturalTransactionBodySchema = z.object({
  requestId: z.string().trim().min(8).max(120),
  description: z.string().trim().min(1).max(240),
  amountCents: z.number().int().safe().refine((value) => value !== 0, 'Valor deve ser diferente de zero.'),
  date: calendarYmdSchema,
  source: z.object({
    kind: z.enum(['account', 'card']),
    name: z.string().trim().min(1).max(120),
  }).strict(),
}).strict();
export type PrepareNaturalTransactionBody = z.infer<typeof prepareNaturalTransactionBodySchema>;

const compactExistingSchema = z.object({
  date: calendarYmdSchema,
  description: z.string(),
  amountCents: z.number().int().safe(),
});

const reconciliationEvidenceSampleSchema = z.object({
  date: calendarYmdSchema,
  description: z.string().min(1).max(240),
  category: z.string().min(1).max(120),
}).strict();

export const reconciliationEvidenceSchema = z.object({
  kind: z.enum(['exact_match', 'duplicate_suspected', 'merchant_rule', 'confirmed_history', 'special_rule', 'jev', 'conflict', 'none']),
  detail: z.string().min(1).max(240),
  observations: z.number().int().positive().optional(),
  samples: z.array(reconciliationEvidenceSampleSchema).max(3).optional(),
});

export const reconciliationSuggestionSchema = z.object({
  title: z.string().min(1).max(120),
  description: z.string().max(240),
  categoryId: z.string().min(1).nullable(),
  category: z.string().min(1).max(120).nullable(),
  parentCategory: z.string().min(1).max(120).nullable(),
  confidence: z.enum(['high', 'learned', 'review_required']),
  evidence: z.array(reconciliationEvidenceSchema).min(1).max(5),
});

export const previewStatementReconciliationRowSchema = normalizedStatementRowSchema.extend({
  status: z.enum(['existing', 'duplicate_suspected', 'candidate', 'special', 'review_required']),
  specialType: z.enum(['card_payment', 'transfer']).nullable(),
  duplicateCandidates: z.array(compactExistingSchema).max(5),
  suggestion: reconciliationSuggestionSchema,
});

export const previewStatementReconciliationResponseSchema = z.object({
  source: z.object({ kind: z.enum(['account', 'card']), name: z.string() }),
  lastEffectuated: compactExistingSchema.nullable(),
  pendingExisting: z.array(compactExistingSchema),
  summary: z.object({
    total: z.number().int().nonnegative(),
    existing: z.number().int().nonnegative(),
    duplicateSuspected: z.number().int().nonnegative(),
    candidate: z.number().int().nonnegative(),
    special: z.number().int().nonnegative(),
    reviewRequired: z.number().int().nonnegative(),
  }),
  rows: z.array(previewStatementReconciliationRowSchema).max(200),
});
export type PreviewStatementReconciliationResponse = z.infer<typeof previewStatementReconciliationResponseSchema>;

const confirmedBatchCheckpointSchema = z.object({
  batchId: z.string().min(1),
  confirmedAt: z.string().min(1),
  periodTo: calendarYmdSchema,
  rowCount: z.number().int().nonnegative(),
}).strict();

export const prepareStatementImportResponseSchema = previewStatementReconciliationResponseSchema.extend({
  batchId: z.string().min(1),
  revision: z.number().int().positive(),
  status: z.enum(['draft', 'confirmed']),
  documentSha256: z.string().regex(/^[a-f0-9]{64}$/),
  contentSha256: z.string().regex(/^[a-f0-9]{64}$/),
  checkpoint: z.object({
    lastConfirmedBatch: confirmedBatchCheckpointSchema.nullable(),
    lastEffectuated: compactExistingSchema.nullable(),
  }).strict(),
});
export type PrepareStatementImportResponse = z.infer<typeof prepareStatementImportResponseSchema>;
export const prepareNaturalTransactionResponseSchema = prepareStatementImportResponseSchema;
export type PrepareNaturalTransactionResponse = PrepareStatementImportResponse;

const uniqueSourceRowIdsSchema = z.array(z.string().trim().min(1).max(120)).min(1).max(200).superRefine((ids, ctx) => {
  if (new Set(ids).size !== ids.length) {
    ctx.addIssue({ code: 'custom', message: 'sourceRowIds duplicados.' });
  }
});

const confirmSelectionSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('all') }).strict(),
  z.object({
    mode: z.literal('selected'),
    sourceRowIds: uniqueSourceRowIdsSchema,
  }).strict(),
]);

export const confirmStatementImportBodySchema = z.object({
  revision: z.number().int().positive(),
  idempotencyKey: z.string().trim().min(8).max(120),
  confirmation: z.literal('CONFIRMAR LOTE'),
  selection: confirmSelectionSchema,
}).strict();
export type ConfirmStatementImportBody = z.infer<typeof confirmStatementImportBodySchema>;

export const confirmStatementImportResponseSchema = z.object({
  batchId: z.string().min(1),
  revision: z.number().int().positive(),
  status: z.enum(['draft', 'confirmed']),
  idempotentReplay: z.boolean(),
  summary: z.object({
    selected: z.number().int().nonnegative(),
    created: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    blocked: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
  }).strict(),
  outcomes: z.array(z.object({
    sourceRowId: z.string().min(1),
    status: z.enum(['created', 'skipped', 'blocked', 'failed']),
    reason: z.string().min(1).max(300).nullable(),
    transaction: transactionSchema.nullable(),
  }).strict()).max(200),
}).strict();
export type ConfirmStatementImportResponse = z.infer<typeof confirmStatementImportResponseSchema>;

const statementImportAdjustmentSchema = z.object({
  sourceRowId: z.string().trim().min(1).max(120),
  title: z.string().trim().min(1).max(120).optional(),
  description: z.string().trim().max(240).optional(),
  categoryId: z.string().trim().min(1).max(120).optional(),
  effectuated: z.boolean().optional(),
  duplicateReviewed: z.boolean().optional(),
}).strict().refine((value) => Object.keys(value).some((key) => key !== 'sourceRowId'), {
  message: 'Informe ao menos um ajuste.',
});

export const adjustStatementImportBodySchema = z.object({
  revision: z.number().int().positive(),
  adjustments: z.array(statementImportAdjustmentSchema).min(1).max(200),
}).strict().superRefine((body, ctx) => {
  const ids = body.adjustments.map((adjustment) => adjustment.sourceRowId);
  if (new Set(ids).size !== ids.length) {
    ctx.addIssue({ code: 'custom', path: ['adjustments'], message: 'sourceRowId duplicado.' });
  }
});
export type AdjustStatementImportBody = z.infer<typeof adjustStatementImportBodySchema>;

const adjustedStatementRowSchema = z.object({
  rowIndex: z.number().int().nonnegative(),
  sourceRowId: z.string().min(1),
  date: calendarYmdSchema,
  time: z.string().nullable(),
  originalDescription: z.string().min(1),
  amountCents: z.number().int().safe(),
  balanceCents: z.number().int().safe().nullable(),
  effectuated: z.boolean(),
  duplicateReviewed: z.boolean(),
  previewStatus: z.enum(['existing', 'duplicate_suspected', 'candidate', 'special', 'review_required']),
  specialType: z.enum(['card_payment', 'transfer']).nullable(),
  decision: z.enum(['pending', 'create', 'skip']),
  suggestion: z.object({
    title: z.string().min(1).max(120),
    description: z.string().max(240),
    categoryId: z.string().min(1).nullable(),
    category: z.string().min(1).max(120).nullable(),
    parentCategory: z.string().min(1).max(120).nullable(),
  }).strict(),
}).strict();

export const adjustStatementImportResponseSchema = z.object({
  batchId: z.string().min(1),
  revision: z.number().int().positive(),
  status: z.literal('draft'),
  rows: z.array(adjustedStatementRowSchema).max(200),
}).strict();
export type AdjustStatementImportResponse = z.infer<typeof adjustStatementImportResponseSchema>;

export const discardStatementImportBodySchema = z.object({
  confirmation: z.literal('DESCARTAR RASCUNHO'),
}).strict();
export type DiscardStatementImportBody = z.infer<typeof discardStatementImportBodySchema>;

export const discardStatementImportResponseSchema = z.object({
  batchId: z.string().min(1),
  discarded: z.literal(true),
  deleted: z.boolean(),
  pendingDiscarded: z.number().int().nonnegative(),
}).strict();
export type DiscardStatementImportResponse = z.infer<typeof discardStatementImportResponseSchema>;
